import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp, writeBatch, collection, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { logEntityChanges } from '../../utils/logger';
import { sanitizeString } from '../../utils/sanitization';
import { VALUE_RANGES } from '../forms/OppModal';
import { canAssignOpportunity, getAssignmentUsers, getOpportunityAssignmentUsers } from '../../utils/assignmentUsers';

/**
 * BulkEditModal — Generic bulk edit modal for Tasks and Opportunities.
 *
 * Props:
 *   entityType   - 'task' | 'opportunity'
 *   selectedItems - Array of full entity objects to update
 *   users        - Array of user objects (for assignee dropdown)
 *   user         - Current logged-in user
 *   onClose      - Close callback
 *   onComplete   - Success callback (message)
 */
const BulkEditModal = ({ entityType, selectedItems, users, user, onClose, onComplete }) => {
  const isTask = entityType === 'task';
  const collName = isTask ? 'tasks' : 'opportunities';
  const entityLabel = isTask ? 'Task' : 'Opportunity';

  // Lead source options (loaded from Firestore for opportunities)
  const [leadSourceOptions, setLeadSourceOptions] = useState([]);
  useEffect(() => {
    if (isTask) return;
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, collectionPath('leadSource')));
        const names = snap.docs.map(d => d.data().name).filter(Boolean);
        setLeadSourceOptions([...new Set(names)].sort());
      } catch (err) {
        console.error('[BulkEdit] Failed to load lead sources:', err);
      }
    };
    load();
  }, [isTask]);

  // Track which fields user wants to change
  const [enabledFields, setEnabledFields] = useState({});
  // Track the values for those fields
  const [values, setValues] = useState({
    status: 'Open',
    priority: 'Medium',
    uid: '',
    dueDate: '',
    stage: 'New',
    leadSource: '',
    value: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const toggleField = (field) => {
    setEnabledFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const setValue = (field, val) => {
    setValues(prev => ({ ...prev, [field]: val }));
  };

  const enabledCount = Object.values(enabledFields).filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enabledCount === 0) return;
    if (!isTask && enabledFields.uid && !canAssignOpportunity(users.find(u => u.uid === values.uid))) {
      setError('Select an active Sales Rep or Sales Manager for opportunity assignment.');
      return;
    }

    setSaving(true);
    setError('');
    setProgress({ done: 0, total: selectedItems.length });

    try {
      // Build the update object from enabled fields only
      const updates = {};
      const trackedFields = [];

      if (enabledFields.status && isTask) {
        updates.status = values.status;
        trackedFields.push('status');
      }
      if (enabledFields.priority && isTask) {
        updates.priority = values.priority;
        trackedFields.push('priority');
      }
      if (enabledFields.stage && !isTask) {
        updates.stage = values.stage;
        trackedFields.push('stage');
      }
      if (enabledFields.leadSource && !isTask) {
        updates.leadSource = sanitizeString(values.leadSource, 200);
        trackedFields.push('leadSource');
      }
      if (enabledFields.value && !isTask) {
        updates.value = values.value;
        trackedFields.push('value');
      }
      if (enabledFields.uid) {
        updates.uid = values.uid;
        trackedFields.push('uid');
      }
      if (enabledFields.dueDate) {
        updates.dueDate = values.dueDate || null;
        trackedFields.push('dueDate');
      }

      updates.updatedAt = serverTimestamp();

      // Process in batches of 400 (under Firestore's 500 limit; leaves room for log docs)
      const BATCH_SIZE = 400;
      let done = 0;

      for (let i = 0; i < selectedItems.length; i += BATCH_SIZE) {
        const chunk = selectedItems.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);

        for (const item of chunk) {
          const ref = doc(db, collectionPath(collName), item.id);
          batch.update(ref, updates);
        }

        await batch.commit();
        done += chunk.length;
        setProgress({ done, total: selectedItems.length });
      }

      // Log changes for each item (fire-and-forget for performance)
      const logPromises = selectedItems.map(item =>
        logEntityChanges({
          oldObj: item,
          newObj: { ...item, ...updates },
          fields: trackedFields,
          entityType: entityLabel,
          entityId: item.id,
          user,
          action: `Bulk Updated`,
        }).catch(() => {}) // Don't fail the whole op on a log error
      );
      // Await in background — don't block the modal close
      Promise.all(logPromises);

      onComplete(`Updated ${selectedItems.length} ${entityLabel.toLowerCase()}${selectedItems.length !== 1 ? 's' : ''}`);
      onClose();
    } catch (err) {
      console.error('[BulkEdit] Error:', err);
      setError(`Failed to update: ${err.message}`);
      setSaving(false);
    }
  };

  // Field config for Tasks
  const taskFields = [
    {
      key: 'status',
      label: 'Status',
      render: () => (
        <select
          value={values.status}
          onChange={e => setValue('status', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="Open">Open</option>
          <option value="Completed">Completed</option>
          <option value="Closed">Closed</option>
        </select>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      render: () => (
        <select
          value={values.priority}
          onChange={e => setValue('priority', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
        </select>
      ),
    },
    {
      key: 'uid',
      label: 'Assigned To',
      render: () => (
        <select
          value={values.uid}
          onChange={e => setValue('uid', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="">-- Select --</option>
          {getAssignmentUsers(users).map(u => (
            <option key={u.uid} value={u.uid}>
              {u.displayName || u.email || u.uid}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: () => (
        <input
          type="date"
          value={values.dueDate}
          onChange={e => setValue('dueDate', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        />
      ),
    },
  ];

  // Field config for Opportunities
  const oppFields = [
    {
      key: 'stage',
      label: 'Stage',
      render: () => (
        <select
          value={values.stage}
          onChange={e => setValue('stage', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="New">New</option>
          <option value="Qualified">Qualified</option>
          <option value="Not Qualified">Not Qualified</option>
          <option value="Proposal Sent">Proposal Sent</option>
          <option value="Closed Won">Closed Won</option>
          <option value="Closed Lost">Closed Lost</option>
        </select>
      ),
    },
    {
      key: 'leadSource',
      label: 'Lead Source',
      render: () => (
        <select
          value={values.leadSource}
          onChange={e => setValue('leadSource', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="">-- Select --</option>
          {leadSourceOptions.map(ls => (
            <option key={ls} value={ls}>{ls}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'value',
      label: 'Potential Value',
      render: () => (
        <select
          value={values.value}
          onChange={e => setValue('value', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="">-- Select --</option>
          {VALUE_RANGES.map(range => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'uid',
      label: 'Sales Rep',
      render: () => (
        <select
          value={values.uid}
          onChange={e => setValue('uid', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        >
          <option value="">-- Select --</option>
          {getOpportunityAssignmentUsers(users).map(u => (
            <option key={u.uid} value={u.uid}>
              {u.displayName || u.email || u.uid}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: () => (
        <input
          type="date"
          value={values.dueDate}
          onChange={e => setValue('dueDate', e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 w-full"
        />
      ),
    },
  ];

  const fields = isTask ? taskFields : oppFields;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Bulk Edit {selectedItems.length} {entityLabel}{selectedItems.length !== 1 ? 's' : ''}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <p className="text-sm text-gray-500 mb-4">
            Check the fields you want to change, then set the new value. Only checked fields will be updated.
          </p>

          <div className="space-y-4">
            {fields.map(field => (
              <div key={field.key} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!enabledFields[field.key]}
                  onChange={() => toggleField(field.key)}
                  className="mt-2.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  id={`bulk-${field.key}`}
                />
                <div className="flex-1">
                  <label htmlFor={`bulk-${field.key}`} className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  <div className={enabledFields[field.key] ? '' : 'opacity-40 pointer-events-none'}>
                    {field.render()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {saving && progress.total > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Updating...</span>
                <span>{progress.done} / {progress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || enabledCount === 0}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Updating...' : `Update ${selectedItems.length} ${entityLabel}${selectedItems.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;
